import React, { useEffect, useState } from "react";
import clsx from "clsx";
import {
  Box,
  Card,
  Grid,
  makeStyles,
  FormControl,
  TextField,
  Button,
  Paper,
  FormHelperText,
  Typography,
  FormLabel,
} from "@material-ui/core";
import SaveIcon from "@material-ui/icons/Save";
import { NavLink as RouterLink } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { kmMessages } from "src/constant/Messages";
import MUIRichTextEditor from "mui-rte";
import { DropzoneArea, DropzoneAreaBase } from "material-ui-dropzone";
import { convertToRaw } from "draft-js";
import { stateToHTML } from "draft-js-export-html";
import { stateFromHTML } from "draft-js-import-html";
import TableContainer from "@material-ui/core/TableContainer";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";
import KmFormEditTable from "src/components/common/KmFormEditTable";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import LinkIcon from "@material-ui/icons/Link";
import convertUrlToImageData from "src/utils/images";
import { fillMissingTags } from "src/utils/utils";

const titleMaxLength = 300;
const headerMaxLength = 500;

const formValidation = {
  required: kmMessages.error.form_validation.field_required,
  maxLength: `${kmMessages.error.form_validation.field_maxlengthnum}`,
};

const useStyles = makeStyles((theme) => ({
  root: {
    "& > *": {
      margin: theme.spacing(2),
    },
  },
  formcontrol: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  paddingbottom: {
    paddingBottom: theme.spacing(1),
  },
  paddingtop: {
    paddingTop: theme.spacing(2),
  },
  button: {
    margin: theme.spacing(1),
  },
  rtepaper: {
    paddingBottom: theme.spacing(4),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  rtecontroll: {
    "& > ol": {
      paddingLeft: theme.spacing(4),
    },
  },
  attachmentdialog: {
    minWidth: 500,
  },
  img: {
    padding: theme.spacing(1),
    display: "block",
    maxHeight: 120,
    maxWidth: "auto",
  },
}));

const attachmentCols = [
  {
    label: "L.p.",
    isLp: true,
    isEditable: false,
    width: "5%",
  },
  {
    label: "Typ",
    fieldName: "type",
    isEditable: false,
    width: "5%",
  },
  {
    label: "Nazwa",
    fieldName: "name",
    isEditable: true,
    width: "40%",
  },
  {
    label: "Plik/Odnośnik",
    fieldName: "details",
    isEditable: false,
    width: "40%",
    isHtml: true,
  },
];

const attachmentColsEdit = [
  {
    label: "L.p.",
    isLp: true,
    isEditable: false,
    width: "5%",
  },
  {
    label: "Typ",
    fieldName: "type",
    isEditable: false,
    width: "5%",
  },
  {
    label: "Nazwa",
    fieldName: "name",
    isEditable: true,
    width: "35%",
  },
  {
    label: "Plik/Odnośnik",
    fieldName: "details",
    isEditable: false,
    width: "35%",
    isHtml: true,
  },
  {
    label: "Rozmiar pliku",
    fieldName: "fileSize",
    isEditable: false,
    width: "10%",
  },
];

const Form = ({ onSubmit, news, editMode, ...rest }) => {
  const classes = useStyles();

  const [nextSubmitTry, setNextSubmitTry] = useState(false);
  const [contentValue, setContentValue] = useState(null);
  const [htmlContentValue, setHtmlContentValue] = useState("");
  const [contentEmpty, setContentEmpty] = useState(true);
  const [mainImageFile, setMainImageFile] = useState(null);
  const [mainImageInitialFiles, setMainImageInitialFiles] = useState([]);

  const [images, setImages] = useState([]);
  const [attachments, setAttachments] = useState([]);

  const [openAttachmentDialog, setOpenAttachmentDialog] = React.useState(false);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [attachmentName, setAttachmentName] = useState(null);
  const [attachmentUrlLabel, setAttachmentUrlLabel] = useState(null);
  const [attachmentUrl, setAttachmentUrl] = useState(null);

  const [attachmentNameEmpty, setAttachmentNameEmpty] = useState(false);
  const [attachmentUrlEmpty, setAttachmentUrlEmpty] = useState(false);

  const { handleSubmit, setValue, control, errors } = useForm();

  useEffect(() => {

    if (editMode && news) {
      setValue("title", news.title);
      setValue("header", news.header);
      setContentRteState(news.content);
      if (news.images) {
        setImagesList(news);
      }
      if (news.attachments) {
        setAttachmentsList(news);
      }
      if (news.image) {
        const file = convertUrlToImageData(
          `${news.imagesBaseUrl}${news.image}`,
          news.mainImageFileName
        );
        setMainImageInitialFiles([file]);
      }
    }
    return () => {};
  }, [news]);

  useEffect(() => {
    setTimeout(fillMissingTags(), 1000);
    return () => {};
  }, []);

  const handleContentChange = (event) => {
    let htmlState = stateToHTML(event.getCurrentContent());

    setContentEmpty(!event.getCurrentContent().hasText());

    setHtmlContentValue(htmlState);
  };

  const setContentRteState = (contentHtml) => {
    let contentState = stateFromHTML(contentHtml);
    setContentValue(JSON.stringify(convertToRaw(contentState)));
  };

  const setImagesList = (news) => {
    setImages(
      news.images.map((image) => {
        return {
          dbId: image.id,
          id: image.id,
          type: (
            <img
              className={classes.img}
              alt={`Zdjęcie do artykułu ${image.fileName}`}
              src={`${news.imagesBaseUrl}${image.fileName}`}
            />
          ),
          fileData: null,
          name: image.fileName,
          resName: image.fileName,
          details: image.description,
          fileSize: image.fileSize,
        };
      })
    );
  };

  const setAttachmentsList = (news) => {
    setAttachments(
      news.attachments.map((attachment) => {
        return {
          dbId: attachment.id,
          id: attachment.id,
          type:
            attachment.type === "file" ? (
              <InsertDriveFileIcon />
            ) : attachment.type === "url" ? (
              <LinkIcon />
            ) : (
              <Typography>-</Typography>
            ),
          attachmentType: attachment.type,
          fileData: null,
          name: attachment.description,
          resName: attachment.fileName,
          details:
            attachment.type === "file" ? (
              `<a target="blank" href="${news.attachmentsBaseUrl}${attachment.fileName}">${attachment.description}</a>`
            ) : attachment.type === "url" ? (
              `<a target="blank" href="${attachment.urlValue}">${attachment.urlLabel}</a>`
            ) : (
              <Typography>-</Typography>
            ),
          fileSize: attachment.fileSize,
        };
      })
    );
  };

  const onFormSubmit = (values) => {

    if (!contentEmpty) {
      setContentEmpty(false);
      onSubmit(values, htmlContentValue, mainImageFile, images, attachments);
    } else {
      setContentEmpty(true);
      setNextSubmitTry(true);
    }
  };

  const handleMainImageFileUpload = (files) => {
    if (files.length > 0) {
      setMainImageFile(files[0]);
    }
  };

  const handleClickOpenAttachmentDialog = () => {
    setAttachmentName("");
    setAttachmentUrl("");
    setAttachmentUrlLabel("");
    setAttachmentNameEmpty(false);
    setAttachmentUrlEmpty(false);
    setOpenAttachmentDialog(true);
  };

  const handleCloseAttachmentDialog = () => {
    setOpenAttachmentDialog(false);
  };

  const handleAttachmentNameChange = (event) => {
    setAttachmentName(event.target.value);
  };

  const handleAttachmentUrlLabelChange = (event) => {
    setAttachmentUrlLabel(event.target.value);
  };

  const handleAttachmentUrlChange = (event) => {
    setAttachmentUrl(event.target.value);
  };

  const handleAttachmentsAdd = (newFiles) => {
    newFiles = newFiles.filter(
      (file) => !attachments.find((f) => f.resName === file.file.name)
    );

    let newAttachments = newFiles.map((file, index) => {
      return {
        id: `${new Date().getTime()}${index}`,
        type: <InsertDriveFileIcon />,
        attachmentType: "file",
        fileData: file,
        name: file.file.name,
        resName: file.file.name,
        details: file.file.name,
      };
    });

    setAttachments([...attachments, ...newAttachments]);

  };

  const handleAddAttachmentUrl = () => {
    if (attachmentName && attachmentUrl) {
      setAttachments([
        ...attachments,
        {
          id: `${new Date().getTime()}12`,
          type: <LinkIcon />,
          attachmentType: "url",
          name: attachmentName,
          resName: attachmentName,
          url: attachmentUrl,
          urlLabel: attachmentUrlLabel ? attachmentUrlLabel : attachmentUrl,
          details: `<a target="blank" href="${attachmentUrl}">${
            attachmentUrlLabel ? attachmentUrlLabel : attachmentUrl
          }</a>`,
        },
      ]);
      setOpenAttachmentDialog(false);
    } else {
      !attachmentName
        ? setAttachmentNameEmpty(true)
        : setAttachmentNameEmpty(false);
      !attachmentUrl
        ? setAttachmentUrlEmpty(true)
        : setAttachmentUrlEmpty(false);
    }
  };

  const handleImagesAdd = (newImages) => {

    newImages = newImages.filter(
      (file) => !images.find((f) => f.resName === file.file.name)
    );

    let addedImages = newImages.map((file, index) => {
      return {
        id: `${new Date().getTime()}${index}`,
        type: (
          <img
            className={classes.img}
            alt={`Zdjęcie do artykułu ${file.file.name}`}
            src={file.data}
          />
        ),
        fileData: file,
        name: file.file.name,
        resName: file.file.name,
        details: file.file.name,
      };
    });

    setImages([...images, ...addedImages]);

  };

  return (
    <Card className={clsx(classes.root)}>
      <Box component="main">
        <Grid container className={classes.root}>
          <Grid item xs={12} elevation={2}>
            <form onSubmit={handleSubmit(onFormSubmit)}>
              {news?.parentName ? (
                <FormControl fullWidth className={classes.formcontrol}>
                  <FormLabel
                    className={classes.paddingBottom}
                    component="label"
                  >
                    Tytuł opublikowanego artykułu
                  </FormLabel>
                  <Typography>{news.parentName}</Typography>
                </FormControl>
              ) : (
                <></>
              )}
              <FormControl
                fullWidth
                className={classes.formcontrol}
              >
                <Controller
                  as={
                    <TextField
                      error={errors.title ? true : false}
                      id="title"
                      name="title"
                      label="Tytuł"
                      placeholder="Tytuł"
                      helperText={
                        errors.title &&
                        (formValidation[errors.title.type] || "").replaceAll(
                          "{maxlen}",
                          titleMaxLength
                        )
                      }
                    />
                  }
                  name="title"
                  defaultValue=""
                  control={control}
                  rules={{ required: true, maxLength: titleMaxLength }}
                />
              </FormControl>
              <FormControl fullWidth className={classes.formcontrol}>
                <Controller
                  as={
                    <TextField
                      error={errors.header ? true : false}
                      id="header"
                      name="header"
                      label="Nagłówek"
                      placeholder="Nagłówek"
                      multiline
                      helperText={
                        errors.header &&
                        (formValidation[errors.header.type] || "").replaceAll(
                          "{maxlen}",
                          headerMaxLength
                        )
                      }
                    />
                  }
                  name="header"
                  defaultValue=""
                  control={control}
                  rules={{ required: true, maxLength: headerMaxLength }}
                />
              </FormControl>
              <FormControl
                fullWidth
                className={classes.formcontrol}
                variant="outlined"
              >
                <Box className={classes.paddingbottom}>
                  <FormLabel for="newscontentrte">Treść:</FormLabel>
                </Box>
                <Paper variant="outlined" className={classes.rtepaper}>
                  <MUIRichTextEditor
                    label="Wprowadź treść artykułu"
                    defaultValue={contentValue}
                    className={classes.rtecontroll}
                    id="newscontentrte"
                    controls={[
                      "title",
                      "bold",
                      "italic",
                      "underline",
                      "strikethrough",
                      "highlight",
                      "undo",
                      "redo",
                      "link",
                      "media",
                      "numberList",
                      "bulletList",
                      "quote",
                      "clear",
                    ]}
                    onChange={handleContentChange}
                    inlineToolbar={true}
                  />
                </Paper>
                {nextSubmitTry && contentEmpty ? (
                  <FormHelperText id="news-content-helper" error>
                    {formValidation.required}
                  </FormHelperText>
                ) : (
                  <></>
                )}
              </FormControl>
              <FormControl
                fullWidth
                className={classes.formcontrol}
                variant="outlined"
              >
                <Box className={classes.paddingbottom}>
                  <FormLabel htmlFor="mainFileUpload">
                    Główna grafika:
                  </FormLabel>
                </Box>
                {news?.image ? (
                  <Box className={classes.image}>
                    <img
                      className={classes.img}
                      alt={`Zdjęcie do ${news.title}`}
                      src={`${news.imagesBaseUrl}${news.image}`}
                    />
                  </Box>
                ) : (
                  <></>
                )}
                <DropzoneArea
                  initialFiles={mainImageInitialFiles}
                  acceptedFiles={["image/*"]}
                  name="mainFileUpload"
                  className={classes.fileUpload}
                  onChange={handleMainImageFileUpload}
                  maxFileSize={1048576}
                  previewText="Wybrany plik:"
                  filesLimit={1}
                  dropzoneText="Przeciągnij i upuść plik lub kliknij"
                  inputProps={{ title: "Dodaj plik", id: "mainFileUpload" }}
                  showPreviews={true}
                  showPreviewsInDropzone={false}
                  showFileNamesInPreview={true}
                />
              </FormControl>
              <FormControl
                fullWidth
                className={classes.formcontrol}
                variant="outlined"
                label="Zdjęcia"
              >
                <Box className={classes.paddingbottom}>
                  <FormLabel htmlFor="imagesfileupload">Zdjęcia:</FormLabel>
                </Box>
                <DropzoneAreaBase
                  acceptedFiles={["image/*"]}
                  showAlerts={false}
                  name="imagesfileupload"
                  className={classes.fileUpload}
                  previewText="Wybrane zdjęcia:"
                  maxFileSize={1048576}
                  filesLimit={10}
                  dropzoneText="Przeciągnij i upuść pliki lub kliknij"
                  inputProps={{
                    title: "Dodaj zdjęcia",
                    id: "imagesfileupload",
                  }}
                  showPreviews={false}
                  showPreviewsInDropzone={false}
                  showFileNamesInPreview={false}
                  onAdd={handleImagesAdd}
                />

                <Box display="flex" justifyContent="right" m={2}></Box>
                <TableContainer component={Paper}>
                  <KmFormEditTable
                    columns={editMode ? attachmentColsEdit : attachmentCols}
                    rows={images}
                    caption="Zdjęcia"
                    setRows={setImages}
                  />
                </TableContainer>
              </FormControl>
              <FormControl
                fullWidth
                className={classes.formcontrol}
                variant="outlined"
                label="Załączniki"
              >
                <Box className={classes.paddingbottom}>
                  <FormLabel htmlFor="attachmentsfileupload">Załączniki:</FormLabel>
                </Box>
                <DropzoneAreaBase
                  showAlerts={false}
                  name="attachmentsfileupload"
                  className={classes.fileUpload}
                  previewText="Wybrane pliki:"
                  filesLimit={10}
                  maxFileSize={5242880}
                  dropzoneText="Przeciągnij i upuść pliki lub kliknij"
                  inputProps={{
                    title: "Dodaj pliki",
                    id: "attachmentsfileupload",
                  }}
                  showPreviews={false}
                  showPreviewsInDropzone={false}
                  showFileNamesInPreview={false}
                  onAdd={handleAttachmentsAdd}
                />

                <Box display="flex" justifyContent="right" m={2}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleClickOpenAttachmentDialog}
                  >
                    Dodaj odnośnik
                  </Button>
                </Box>
                <TableContainer component={Paper}>
                  <KmFormEditTable
                    columns={editMode ? attachmentColsEdit : attachmentCols}
                    rows={attachments}
                    caption="Załączniki"
                    setRows={setAttachments}
                  />
                </TableContainer>
                <Dialog
                  className={classes.attachmentdialog}
                  open={openAttachmentDialog}
                  onClose={handleCloseAttachmentDialog}
                  aria-labelledby="form-dialog-title"
                  fullScreen={fullScreen}
                >
                  <DialogTitle id="form-dialog-title">
                    Dodawanie załącznika
                  </DialogTitle>
                  <DialogContent>
                    {/* <DialogContentText>
                        To subscribe to this website, please enter your email
                        address here. We will send updates occasionally.
                      </DialogContentText> */}
                    <TextField
                      error={attachmentNameEmpty}
                      helperText={
                        attachmentNameEmpty ? formValidation.required : ""
                      }
                      autoFocus
                      margin="dense"
                      id="attname"
                      label="Nazwa załącznika"
                      value={attachmentName}
                      onChange={handleAttachmentNameChange}
                      type="text"
                      maxLength={255}
                      fullWidth
                    />
                    <TextField
                      error={attachmentUrlEmpty}
                      helperText={
                        attachmentUrlEmpty ? formValidation.required : ""
                      }
                      margin="dense"
                      id="atturlurl"
                      value={attachmentUrl}
                      onChange={handleAttachmentUrlChange}
                      label="Adres odnośnika"
                      type="text"
                      maxLength={255}
                      fullWidth
                    />

                    <TextField
                      margin="dense"
                      id="atturllabel"
                      label="Etykieta odnośnika"
                      value={attachmentUrlLabel}
                      onChange={handleAttachmentUrlLabelChange}
                      type="text"
                      maxLength={255}
                      fullWidth
                    />
                  </DialogContent>
                  <DialogActions>
                    <Button
                      onClick={handleCloseAttachmentDialog}
                      color="primary"
                    >
                      Anuluj
                    </Button>
                    <Button onClick={handleAddAttachmentUrl} color="primary">
                      Dodaj
                    </Button>
                  </DialogActions>
                </Dialog>
              </FormControl>

              <Box className={classes.paddingtop}>
                <Button
                  color="primary"
                  className={classes.button}
                  component={RouterLink}
                  to="/aktualnosci/wersjerob"
                >
                  Anuluj
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  className={classes.button}
                  endIcon={<SaveIcon />}
                  type="submit"
                >
                  Zapisz
                </Button>
              </Box>
            </form>
          </Grid>
        </Grid>
      </Box>
    </Card>
  );
};

export default Form;
